import { ActionFunctionArgs, LoaderFunctionArgs } from '@remix-run/cloudflare';
import { user_anonymous } from '~/lib/auth';
import { db_user_create } from '~/.server/drizzle/repository/user';
import { useActionData, useLoaderData } from '@remix-run/react';
import { remix_schema_post } from '~common/.server/remix';
import * as v from 'valibot';
import form from '~/components/valibot/form';
import ValibotForm from '~/components/valibot/ValibotForm';
import FormGroup from '~/components/form/FormGroup';
import Message from '~/components/Message';
import ValibotFieldInput from '~/components/valibot/ValibotFieldInput';
import ValibotButton from '~/components/valibot/ValibotButton';
import NavigationButton from '~/components/interactive/NavigationButton';
import { Row } from '~/components/layout/Sections';
import Heading from '~/components/layout/Heading';
import { inngest_send_event } from '~/.server/inngest';
import { InngestEvent } from '~inngest/schema';
import { customer_portal_disabled_message, tenant_require } from '~/tenants/common/util.server';
import { remix_brand_id } from '~lib/.server/request';
import CustomerPortalDisabled from '~/components/CustomerPortalDisabled';

const schema = v.object({
  email: form.email(),
});

export async function loader(args: LoaderFunctionArgs) {
  await user_anonymous(args);

  const tenant = tenant_require(args);
  const message = customer_portal_disabled_message(tenant);

  if (message) {
    return { disabled: true as const, message };
  }

  return { disabled: false as const };
}

export async function action(args: ActionFunctionArgs) {
  const tenant = tenant_require(args);
  const message = customer_portal_disabled_message(tenant);

  if (message) {
    return { success: false as const, error: message };
  }

  try {
    const { email } = await remix_schema_post(args, schema);

    const [user] = await db_user_create({
      ctx: args,
      email,
      data: { verified_send: true, brand: remix_brand_id(args) },
    });

    await inngest_send_event(args, InngestEvent.USER_DIRTY, {
      tenant: tenant.id,
      user_id: user.id,
    });

    return { success: true as const };
  } catch (error) {
    const err = error as Error;

    if (err.message?.includes('UNIQUE constraint failed: users.email')) {
      return { success: false as const, error: 'This email address is already registered, please try to login.' };
    }

    throw error;
  }
}

export default function Register() {
  const data = useLoaderData<typeof loader>();
  const posted = useActionData<typeof action>();

  if (data.disabled) {
    return <CustomerPortalDisabled message={data.message} compact />;
  }

  if (posted?.success) {
    return <Message type="success">Please check your email for a link to continue the registration process.</Message>;
  }

  return (
    <>
      <div className="pb-6 max-sm:hidden text-left">
        <Heading title="Register" size="xl" />
      </div>
      <ValibotForm schema={schema}>
        <FormGroup>
          {posted?.error && <Message type="error">{posted.error}</Message>}
          <ValibotFieldInput
            name="email"
            label="Email"
            type="email"
            autoFocus
            placeholder="Please enter an email address here."
          />
          <Row between>
            <ValibotButton>Register</ValibotButton>
            <NavigationButton to="/login" variant="outline">
              Back to Login
            </NavigationButton>
          </Row>
        </FormGroup>
      </ValibotForm>
    </>
  );
}
